import { type DropdownOption } from '~/_shared/baseComponents/dropdown';
import { type MapId } from '~/_shared/types/map';
import type { SpreadsheetMatchupColumns } from '~/_shared/types/spreadsheetData/matchupData';
import {
  type ColumnId,
  type ColumnName,
  type RealSpreadsheetColumn,
} from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { compareRealSpreadsheetColumns } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import type { RealSpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { stringObjectEntries } from '~/_shared/utils/object/objectEnumerators';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type SpreadsheetMapInfo } from '~/map/listing/item/mapListingItem';
import type { RealSpreadsheetResponseId } from '~/map/map.repository';
import type { MatchupColumnDropdownOption } from './useMatchupColumnsSection';

export type PerMapIdColumn = Record<MapId, RealSpreadsheetColumn | null>;

export type Row = Readonly<{
  selectedSourceColumnPerMap: PerMapIdColumn;
  targetColumnName: string | null;
  isRemovable: boolean;
  isIncluded: boolean;
}>;

export type RowWithExtraData = Readonly<Row & {
  availableTargetColumnNames: DropdownOption<string | null>[];
  onlyOneSourceMap: boolean;
}>;

export type IntersectsMapItem = {
  id: number;
  name: string;
  isCurrentMap: boolean;
  isDisabled: boolean;
  realSpreadsheetId?: number;
  spreadSheets?: ReadonlyArray<{
    spreadsheetId: number;
    isPrimary: boolean;
    realSpreadsheets: ReadonlyArray<{ realSpreadsheetId: number }>;
  }>;
};

export function findAnotherIndex<T>(input: T[], findFnc: (value: T) => boolean, notThisIndex: number): number {
  return input.findIndex((value, index) => findFnc(value) && index !== notThisIndex);
}

export const getAnotherRowWithSameColumnIndex = (rows: Row[], column: RealSpreadsheetColumn, updatedRowIndex: number, mapId: number) => {
  return findAnotherIndex(rows, (row) => row.selectedSourceColumnPerMap?.[mapId]?.id === column.id, updatedRowIndex);
};

export const getRowUsedOtherData = (rows: Row[], value: string, updatedRowIndex: number) => {
  return findAnotherIndex(rows, (row) => row.targetColumnName === value, updatedRowIndex);
};

const replaceRow = (rows: Row[], index: number, newValueFunction: (prev: Row) => Row): Row[] => {
  const row = rows[index];

  if (!row) {
    return rows;
  }

  return [
    ...rows.slice(0, index),
    newValueFunction(row),
    ...rows.slice(index + 1),
  ];
};

const createChangeSelectedColumnFunction = (mapId: number, selectedColumn: RealSpreadsheetColumn | null) => (row: Row): Row => ({
  ...row,
  selectedSourceColumnPerMap: {
    ...row.selectedSourceColumnPerMap,
    [mapId]: selectedColumn,
  },
  targetColumnName: selectedColumn === null ? row.targetColumnName : null, // if a column is removed from row, we want to keep the name
});

const createClearSelectedColumnFunction = (mapId: number) => (row: Row): Row => ({
  ...row,
  selectedSourceColumnPerMap: {
    ...row.selectedSourceColumnPerMap,
    [mapId]: null,
  },
}
);

const clearTargetColumn = (row: Row) => ({
  ...row,
  targetColumnName: null,
});

const createFixTargetColumnFunction = (removedSourceColumn: RealSpreadsheetColumn | null) => (row: Row): Row => {
  const wasUsedSourceColumnRemoved = row.targetColumnName === removedSourceColumn?.name;
  if (!wasUsedSourceColumnRemoved) {
    return row;
  }

  return clearTargetColumn(row);
};

const resolveConflictBetweenRows = (
  rows: Row[], conflictingColumn: RealSpreadsheetColumn, index: number,
  mapId: number, selectedColumn: RealSpreadsheetColumn | null) => {
  const conflictingRowIndex = getAnotherRowWithSameColumnIndex(rows, conflictingColumn, index, mapId);
  const isConflict = conflictingRowIndex !== -1;
  if (!isConflict) {
    return rows;
  }

  const rowsWithoutDuplicateUsage = replaceRow(rows, conflictingRowIndex, createClearSelectedColumnFunction(mapId));
  return replaceRow(rowsWithoutDuplicateUsage, conflictingRowIndex, createFixTargetColumnFunction(selectedColumn));
};

const removeRowsWithAllNoneOptions = (rows: Row[]): Row[] => {
  return rows.filter(row => !Object.values(row.selectedSourceColumnPerMap).every(c => !c));
};

export const addMissingSourceColumnsToRows = (
  rows: Row[],
  sourceColumnsPerMap: Record<MapId, MatchupColumnDropdownOption[]>,
  maps: ReadonlyArray<{
    id: number;
    name: string;
  }>,
  isIncluded: boolean = true,
): Row[] => {
  const usedColumnNames = new Set<ColumnName>(rows.map(r => r.targetColumnName).filter(notNullsy));

  const missingSourceColumnsRows: Row[] = maps
    .reduce<{ rows: Row[]; usedColumnNames: Set<string>}>((acc, map) => {

      // For each map's columns
      const allUnusedMapColumns = sourceColumnsPerMap[map.id]
        // remove the None options
        ?.filter(column => column.value !== null)
        // Check for those columns that are not listed in any rows
        .filter(column =>
          !rows.some(row => {
            const selectedForMap = row.selectedSourceColumnPerMap[map.id];
            return selectedForMap && column.value && compareRealSpreadsheetColumns(selectedForMap, column.value);
          })
        );

      if (!allUnusedMapColumns?.length) {
        return acc;
      }

      // Convert them into new rows
      for (const column of allUnusedMapColumns) {
        const newSelectedOptionPerMap: PerMapIdColumn = createRecordWithDefaultForMaps(maps, null);
        newSelectedOptionPerMap[map.id] = column.value;
        const targetColumnName = acc.usedColumnNames.has(column.name) ? null : column.name;

        if (targetColumnName) {
          acc.usedColumnNames.add(targetColumnName);
        }

        acc.rows.push({
          targetColumnName,
          selectedSourceColumnPerMap: newSelectedOptionPerMap,
          isRemovable: true,
          isIncluded,
        });
      }

      return acc;
    }, {
      rows: [],
      usedColumnNames,
    }).rows;

  return [
    ...rows,
    ...missingSourceColumnsRows,
  ];
};

const isMaybeDuplicateColumnUsage = (selectedColumn: RealSpreadsheetColumn | null): selectedColumn is RealSpreadsheetColumn => {
  return !!selectedColumn;
};

export const selectColumn = (
  rows: Row[], selectedColumn: RealSpreadsheetColumn | null, mapId: number, rowIndex: number,
  sourceColumnsPerMap: Record<MapId, MatchupColumnDropdownOption[]>,
  maps: ReadonlyArray<{
    id: number;
    name: string;
  }>
): Row[] => {
  const changeSelectedColumnFunction = createChangeSelectedColumnFunction(mapId, selectedColumn);
  const rowsWithUpdatedColumn = replaceRow(rows, rowIndex, changeSelectedColumnFunction);

  if (!isMaybeDuplicateColumnUsage(selectedColumn)) {
    return addMissingSourceColumnsToRows(removeRowsWithAllNoneOptions(rowsWithUpdatedColumn), sourceColumnsPerMap, maps);
  }

  const rowsWithUniqueColumnsUsage = resolveConflictBetweenRows(rowsWithUpdatedColumn, selectedColumn, rowIndex, mapId, selectedColumn);

  return addMissingSourceColumnsToRows(removeRowsWithAllNoneOptions(rowsWithUniqueColumnsUsage), sourceColumnsPerMap, maps);
};

export const addNewRow = (rows: Row[], maps: ReadonlyArray<{
  id: number;
  name: string;
}>): Row[] => {
  const emptySelectedOptionPerMap: PerMapIdColumn = {};
  for (const map of maps) {
    emptySelectedOptionPerMap[map.id] = null;
  }
  return [...rows, {
    targetColumnName: null,
    selectedSourceColumnPerMap: emptySelectedOptionPerMap,
    isRemovable: true,
    isIncluded: true,
  }];
};

export const removeRow = (
  rows: Row[], index: number,
  sourceColumnsPerMap: Record<MapId, MatchupColumnDropdownOption[]>,
  maps: IntersectsMapItem[]
) => {
  return addMissingSourceColumnsToRows(
    [...rows.slice(0, index), ...rows.slice(index + 1)],
    sourceColumnsPerMap,
    maps
  );
};

export const getMapIdFromPrimarySpreadsheetId = (mapList: IntersectsMapItem[], spreadsheetId: number) => {
  for (const map of mapList) {
    const spreadsheets = map.spreadSheets || [];
    const spreadsheetIds = spreadsheets.map(spreadsheet => spreadsheet.spreadsheetId);
    if (spreadsheetIds.includes(spreadsheetId)) {
      return map.id;
    }
  }
  return null;
};

export const getMapFromRealSpreadsheetId = (mapList: IntersectsMapItem[], spreadsheetId: number): IntersectsMapItem | null => {
  for (const map of mapList) {
    if (map.isCurrentMap) {
      if (map.realSpreadsheetId === spreadsheetId) {
        return map;
      }
    }
    else {
      const spreadsheets = map.spreadSheets || [];
      let allSpreadsheetIds: number[] = [];
      spreadsheets.forEach(spreadsheet => {
        const realSpreadsheets = spreadsheet.realSpreadsheets;
        realSpreadsheets.forEach((item) => {
          allSpreadsheetIds = [...allSpreadsheetIds, item.realSpreadsheetId];
        });
      });
      if (allSpreadsheetIds.includes(spreadsheetId)) {
        return map;
      }
    }
  }
  return null;
};

export const getSpreadsheetIdFromMapId = (mapList: IntersectsMapItem[], mapId: number) => {
  for (const map of mapList) {
    if (map.id === mapId) {
      const spreadsheets = map.spreadSheets || [];
      const primarySpreadsheet = spreadsheets.filter((spreadsheet: SpreadsheetMapInfo) => spreadsheet.isPrimary);
      return primarySpreadsheet?.[0]?.spreadsheetId || null;
    }
  }
  return null;
};

export const getRealSpreadsheetIdFromMapId = (mapList: IntersectsMapItem[], mapId: number) => {
  for (const map of mapList) {
    if (map.id === mapId) {
      const spreadsheets = map.spreadSheets || [];
      const primarySpreadsheet = spreadsheets.filter((spreadsheet: SpreadsheetMapInfo) => spreadsheet.isPrimary);
      if (primarySpreadsheet.length && primarySpreadsheet[0]?.realSpreadsheets.length) {
        return primarySpreadsheet[0]?.realSpreadsheets[0]?.realSpreadsheetId ?? null;
      }
      return null;
    }
  }
  return null;
};

export const getPrimarySpreadsheetIdFromMapId = (mapList: IntersectsMapItem[], mapId: number) => {
  for (const map of mapList) {
    if (map.id === mapId) {
      const spreadsheets = map.spreadSheets || [];
      const primarySpreadsheet = spreadsheets.filter((spreadsheet: SpreadsheetMapInfo) => spreadsheet.isPrimary);
      if (primarySpreadsheet.length) {
        return primarySpreadsheet[0]?.spreadsheetId ?? null;
      }
      return null;
    }
  }
  return null;
};

export const hasRowOnlyOneSourceMap = (row: Row, maps: IntersectsMapItem[]) => {
  const definedColumns = maps.reduce((count, m) => {
    const sourceColumn = row.selectedSourceColumnPerMap[m.id];
    return count + (sourceColumn ? 1 : 0);
  }, 0);

  return definedColumns === 1;
};

export const createRecordWithDefaultForMaps = <UValue extends {id: number}, TValue>(items: ReadonlyArray<UValue>, defaultValue: TValue) =>
  items.reduce((lookup, item) => {
    lookup[item.id] = defaultValue;

    return lookup;
  }, {} as Record<number, TValue>);

export const createUsedColumnKeyForMap = (mapId: number, columnKey: string) => {
  return `${mapId}_${columnKey}`;
};

export const getTargetColumnForColumn = ({ columnName, columns, mapId, usedKeys }: Readonly<{
  columnName: ColumnName | null;
  columns: Readonly<Record<ColumnId, ColumnName>>;
  mapId: MapId;
  usedKeys: ReadonlySet<string>;
}>): ColumnId | null =>
  Object.keys(columns).find(columnId => {
    const shouldBeUsed = !usedKeys.has(createUsedColumnKeyForMap(mapId, columnId));
    return shouldBeUsed && columns[columnId] === columnName;
  }) || null;

export const extractRealSpreadsheetIdFromKey = (spreadsheetKey: RealSpreadsheetResponseId) =>
  Number(spreadsheetKey.slice(spreadsheetKey.indexOf('_') + 1));

export const createKeyFromRealSpreadsheetId = (realSpreadsheetId: number): RealSpreadsheetResponseId =>
  `spreadsheet_${realSpreadsheetId}`;

export const createColumnIdFromNumber = (id: number) =>
  `column_${id}`;

export const spreadsheetMatchupColumnsToRealSpreadsheetIds = (
  realSpreadsheetId: RealSpreadsheetId,
  columns: SpreadsheetMatchupColumns,
): ReadonlyArray<RealSpreadsheetColumn> =>
  stringObjectEntries(columns).map(([columnId, columnName]) => ({
    id: columnId,
    name: columnName,
    realSpreadsheetId,
  }));
